import { narrationTimingGuidance } from 'js/src/agent/skills/assetTimingGuidance';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const voiceoverVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.voiceoverVideoBasics,
    description:
        'Shared multi-tool procedures for voiceover/talking-head scene groups — overlays, restructuring, reordering, re-timing, timing text and icons to the narration, and coverage repair. Load whenever building a voiceover video (before working with the narration), and whenever editing one.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Voiceover Video Basics

Shared procedures for voiceover and talking-head scene groups — when to reach for
which tool.

## Captions and bottom-of-screen text
Captions render along the bottom of the screen. When captions are enabled for a
scene group, pick designs whose text sits away from the bottom — a bottom text
callout ends up colliding with the captions — and keep any text you position
manually clear of that band.

## Overlay vs structural break vs reorder
Target a scene group by its \`scene_group_id\` (shown in each \`@@ Scene Group <id> @@\`
header), never by a positional number.
- **\`${TOOL_NAMES.addVoiceoverScene}\`** — a visual overlay while the speech keeps flowing.
  Pass every overlay for a scene group in ONE call, as a \`scenes\` array covering each
  word range in order — not one call per overlay. Keep each range to a single beat —
  about a sentence, ~4 seconds or ~15 words; much past that on one overlay holds the
  frame too long, so split it at a clause or sentence boundary.
- **\`${TOOL_NAMES.splitSceneGroup}\`** — an actual structural break: a section that needs to be
  independently editable, a silent gap, or content you'll reorder.
- **\`${TOOL_NAMES.moveSceneGroup}\`** — reorder after splitting. Position the group with
  \`after_scene_group_id\` (it lands immediately after that group; omit to move it to the
  very start).
Don't fake a break by chaining overlays, and don't split a group just to add one
overlay.

## Section headings that break up a voiceover
Requests like "section titles between the parts" or "break the video into segments"
call for silent, no-voiceover cards *between* the spoken parts. Structural fact that
governs how: a voiceover scene group plays as one continuous run — every
\`${TOOL_NAMES.addVoiceoverScene}\` overlay lives INSIDE it. A silent card (a regular scene added
with \`${TOOL_NAMES.addScene}\`) is a SEPARATE group, so it can only sit before or after a voiceover
group, never between that group's overlays. You therefore cannot segment narration by
dropping cards between overlays; the overlays are one contiguous group and every card
collapses to the start or end of it.

To place a silent card between two spoken parts, split the narration there first:
1. \`${TOOL_NAMES.splitSceneGroup}\` at every section boundary in one call — K spoken sections need
   K-1 split points, turning the one voiceover group into one group per section.
2. Add each card with \`${TOOL_NAMES.addScene}\`, \`after_scene_group_id\` = the voiceover group it
   follows, so it lands in the seam between two spoken groups (omit the anchor to put a
   card at the very start).

The number of cards you can place mid-narration equals the number of split points you made:
split zero times and every card piles up at the start or end of the single block. Split
first, then drop each card into its seam, rather than adding all the cards up front and
trying to reposition them.

<example goal="a title, then 3 narrated sections each led by a silent card">
  Full narration starts as one group. Split it at the 2 internal section boundaries →
  3 voiceover groups (S1, S2, S3). Add the title card at the start; add card "1" after
  the title (before S1), card "2" after S1, card "3" after S2. Result:
  title · card1 · S1 · card2 · S2 · card3 · S3.
</example>

## Coverage repair
Every transcript word in a **voiceover** scene group must be covered by an overlay —
uncovered ranges render as raw waveform. Deleting an overlay (\`${TOOL_NAMES.deleteScene}\`) or shrinking
one (\`${TOOL_NAMES.setSceneRange}\`) leaves its span uncovered rather than reflowing its neighbours, so
the gap is yours to fill: extend an adjacent overlay onto it with \`${TOOL_NAMES.setSceneRange}\` — often
the cleaner fix, since it reuses that overlay's existing media and text — or add a fresh overlay with
\`${TOOL_NAMES.addVoiceoverScene}\` when the gap wants its own visual. Either way cover exactly the words
the \`overlay_coverage\` lint names — never the whole transcript, which would swallow the neighbouring
overlays.

## Re-timing an overlay
\`${TOOL_NAMES.setSceneRange}(scene_id, words)\` re-times an existing overlay so it covers exactly the
words you pass — grow, shrink, or shift it while keeping its text/media. Growing it over a neighbour's
words pushes that neighbour back; shrinking it frees the words it drops — they become uncovered and the
waveform base shows there, rather than a neighbour stretching to swallow them. In a **voiceover** group,
re-cover the freed words unless the user explicitly asked to expose the base — extend an adjacent overlay
back onto them, or add a fresh \`${TOOL_NAMES.addVoiceoverScene}\` if they want their own content. On a talking-head or
avatar group the exposed presenter is the intended look — leave it. The new range must overlap the
overlay's current coverage; to move an overlay somewhere unrelated, \`${TOOL_NAMES.deleteScene}\` and
\`${TOOL_NAMES.addVoiceoverScene}\` there instead.

${narrationTimingGuidance()}

## Long narration
For narration longer than about 3 minutes, load \`${SKILL_NAMES.buildLongVideo}\`: it splits the
narration into sections and fills them in parallel with subagents.

## Targeting overlays
Every \`${TOOL_NAMES.addVoiceoverScene}\` result reports the \`scene_id\` it created — record and use
those scene_ids when setting the overlay's text and media. Never infer a scene's position
by counting; that drifts and lands content one scene off from its narration.`;
    },
};
